import { type MutationHookOptions } from '@apollo/react-hooks';
import { useContext, useRef } from 'react';
import { isFedRamp, isIsolatedCloud, isolatedCloudDomain } from '@atlassian/atl-context';
import {
	type ContextId,
	type ExtensionId,
	type ProductEnvironment,
	type ForgeCDNEnvironment,
} from '@atlassian/forge-ui-types';
import { base64ToBlob, blobToBase64, getCDNEnvironment } from '../../../utils';
import { fg } from '@atlaskit/platform-feature-flags';
import { createAppAri } from '@atlassian/cs-ari';
import { getPerformanceMark } from '../../../performance-profiling';
import { usePerformanceAnalytics } from '../../../performance-profiling/usePerformanceAnalytics';
import { useFetchAuthToken, type AuthToken } from './useFetchAuthToken';
import { getTenantRegion } from '../utils/getTenantRegion';
import { ReactRelayContext } from 'react-relay';
import { UserAccessContext } from '../../../context/user-access';

const validProducts = ['confluence', 'jira', 'bitbucket'] as const;
export type Product = (typeof validProducts)[number];

export type OAuthFetchClient = (
	restPath: string,
	product: Product,
	init: Omit<RequestInit, 'body'> & { body?: string | null },
	isMultipartFormData: boolean,
) => Promise<Response>;

export interface UseProductFetchClientOptions {
	appId: string;
	cloudId?: string;
	contextIds: ContextId[];
	environment: ProductEnvironment;
	extensionId: ExtensionId;
	isAnonymousAccessSupportedByApp: boolean;
	options?: MutationHookOptions;
	renderThreeLOPrompt: (retry: () => Promise<void>) => Promise<void>;
	workspaceId?: string;
}

const apiGatewayUrlMap: Record<ForgeCDNEnvironment, string> = {
	dev: 'api.stg.atlassian.com',
	stg: 'api.stg.atlassian.com',
	prod: 'api.atlassian.com',
};

const getApiGatewayUrl = (environment: ForgeCDNEnvironment): string => {
	const apiBase = apiGatewayUrlMap[environment];
	const url = isFedRamp() ? apiBase.replace('atlassian.com', 'atlassian-us-gov-mod.com') : apiBase;

	if (!url) {
		throw new Error(`Invalid environment: ${environment}`);
	}

	return url;
};

const getBaseUrlForIsolatedCloud = (product: Product, cloudId?: string) => {
	if (product === 'bitbucket') {
		throw new Error('product not supported in Isolated Cloud yet.');
	}
	const apiBase = `api.${isolatedCloudDomain()}`;
	return `https://${apiBase}/ex/${product}/${cloudId}`;
};

function getBaseUrlForCommercialAndFedRamp(
	environment: ForgeCDNEnvironment,
	product: Product,
	cloudId?: string,
): string {
	if (product === 'bitbucket') {
		return environment === 'prod'
			? 'https://api.bitbucket.org'
			: 'https://api.integration.bb-inf.net';
	}
	const apiBase = getApiGatewayUrl(environment);
	return `https://${apiBase}/ex/${product}/${cloudId}`;
}

function getBaseUrl(environment: ForgeCDNEnvironment, product: Product, cloudId?: string): string {
	if (fg('forge-ui-oasis-enabled')) {
		return isIsolatedCloud()
			? getBaseUrlForIsolatedCloud(product, cloudId)
			: getBaseUrlForCommercialAndFedRamp(environment, product, cloudId);
	}
	return getBaseUrlForCommercialAndFedRamp(environment, product, cloudId);
}

function createProductUrl(
	environment: ForgeCDNEnvironment,
	product: Product,
	restPath: string,
	cloudId?: string,
): string {
	const baseUrl = getBaseUrl(environment, product, cloudId);
	const fullUrl =
		// bitbucket requests could be made with absolute URLs
		// e.g. when following href links in API responses
		product === 'bitbucket' && restPath.startsWith(`${baseUrl}/`)
			? restPath
			: `${baseUrl}${restPath}`;
	const productUrl = new URL(fullUrl).toString();

	if (!productUrl.startsWith(`${baseUrl}/`)) {
		throw new Error('Invalid product URL');
	}

	return productUrl;
}

export function isTokenValid(token: AuthToken | undefined): token is AuthToken {
	return !!(token && token.expiry > Date.now());
}

function isProduct(product: unknown): product is Product {
	return validProducts.includes(product as Product);
}

export function isBinaryContentType(contentType: string | undefined | null): boolean {
	return Boolean(
		contentType && !contentType.includes('application/json') && contentType.search(/text\//) === -1,
	);
}

function parseFormData(formLike: Record<string, string>): FormData {
	const form = new FormData();

	for (const [key, value] of Object.entries(formLike)) {
		if (key === 'file') {
			const fileName = formLike['__fileName'];
			const mimeType = formLike['__fileType'];

			// convert back from base64 to File (Blob)
			const blob = base64ToBlob(value, mimeType);
			const file = new File([blob], fileName, { type: mimeType });

			form.append('file', file);
		} else {
			form.append(key, value);
		}
	}

	// remove request metadata properties
	form.delete('__fileName');
	form.delete('__fileType');

	return form;
}

const requestProduct = async (url: string, init?: RequestInit): Promise<Response> => {
	const response = await fetch(url, init);

	const isAttachment = isBinaryContentType(response.headers.get('content-type'));
	if (response.ok && response.body !== null && response.status !== 204 && isAttachment) {
		const b64Content = await blobToBase64(await response.blob());

		return new Response(b64Content, {
			status: response.status,
			statusText: response.statusText,
			headers: response.headers,
		});
	}

	return response;
};

export function useProductFetchClient({
	appId,
	extensionId,
	environment,
	cloudId,
	workspaceId,
	renderThreeLOPrompt,
	options,
	contextIds = [],
	isAnonymousAccessSupportedByApp = false,
}: UseProductFetchClientOptions): OAuthFetchClient {
	const { captureEvent } = usePerformanceAnalytics();
	const userAccess = useContext(UserAccessContext);

	const authTokenRef = useRef<AuthToken | undefined>(undefined);
	const isUserAnonymous = userAccess?.isUserAnonymous ?? false;
	const anonymousRequestsEnabled = useRef<boolean>(
		isUserAnonymous && isAnonymousAccessSupportedByApp,
	);

	const relayContext = useContext(ReactRelayContext);
	const relayEnvironment = relayContext?.environment;
	const fetchOAuthToken = useFetchAuthToken({ renderThreeLOPrompt });

	/**
	 * Workspace based products like Trello and Bitbucket will not pass in cloudId,
	 * they will pass in workspaceId instead.
	 */
	if (!cloudId && !workspaceId) {
		return async () => {
			throw new Error(
				'Product APIs are not available in this module. Remove requestConfluence, requestJira or requestBitbucket from your code.',
			);
		};
	}

	const cdnEnvironment = getCDNEnvironment(environment);

	return async (restPath, product, init, isMultipartFormData) => {
		if (!isProduct(product)) {
			throw new Error(`Invalid product: ${product}`);
		}

		const requestAuthStartMark = getPerformanceMark();
		captureEvent?.({
			type: 'product-api-request-initiated',
			elapsed: requestAuthStartMark,
		});

		const prerequisites: Array<Promise<unknown>> = [];
		const headers = new Headers(init.headers);

		if (fg('forge-ui-enable-region-steering')) {
			// We need to fetch the tenant region to know which region to steer the request to.
			prerequisites.push(
				getTenantRegion({ product, cloudId, relayEnvironment })
					.then((targetRegion) => {
						if (!!targetRegion) {
							headers.set('atl-edge-region-hint', targetRegion);
						}
					})
					// Fallback to no steering.
					.catch(() => null),
			);
		}

		const isAccessValid = anonymousRequestsEnabled.current || isTokenValid(authTokenRef.current);
		if (!isAccessValid) {
			// Non-anonymous user -> fetch an OAuth token for API auth
			prerequisites.push(
				fetchOAuthToken(extensionId, contextIds)
					.then((token) => {
						// Token valid -> use in API calls
						authTokenRef.current = token;
					})
					.catch((e) => {
						// Failure -> fallback to anon access if supported by app
						if (isAnonymousAccessSupportedByApp && fg('forge-ui-allow-anon-rest-calls')) {
							anonymousRequestsEnabled.current = true;
							return;
						}
						// Expecting token -> Rethrow
						throw e;
					}),
			);
		}

		await Promise.all(prerequisites);

		if (!anonymousRequestsEnabled.current) {
			headers.set('Authorization', `Bearer ${authTokenRef.current?.token}`);
		}

		// this header is used to identify requests made by Forge, based on:
		// https://hello.atlassian.net/wiki/x/O5jUIwE?atlOrigin=eyJpIjoiM2NhNjU1MDUxOWJkNDcxNThiNzJjNWU1NzE5Y2FmZTMiLCJwIjoiYyJ9
		const appAri = createAppAri({ appId }).toString();
		headers.set('x-ecosystem-user-agent', `forge ${appAri}`);

		const body = isMultipartFormData ? parseFormData(JSON.parse(init.body as string)) : init.body;

		if (isMultipartFormData) {
			// let the fetch api correctly set the multipart/form-data content-type
			// https://github.com/github/fetch/issues/505#issuecomment-293064470
			headers.delete('content-type');
		}

		const request = requestProduct(createProductUrl(cdnEnvironment, product, restPath, cloudId), {
			// Defensive assumption: only token & anonymous auth is supported; prevent unintended
			// fallback to other auth mechanisms by omitting credentials (e.g. cookies).
			credentials: fg('forge-ui-allow-anon-rest-calls') ? 'omit' : undefined,
			...init,
			body,
			headers,
		});

		if (fg('forge-ui-app-perf-tracking-enabled')) {
			const requestAuthEndMark = getPerformanceMark();
			request.then(() => {
				const requestEndMark = getPerformanceMark();
				captureEvent?.({
					type: 'product-api-requested',
					duration: requestEndMark - requestAuthStartMark,
					authDuration: requestAuthEndMark - requestAuthStartMark,
					elapsed: requestEndMark,
				});
			});
		}

		return request;
	};
}
